import React, { useEffect } from 'react';
import LegalLayout from '../components/legal/LegalLayout';

const SECTIONS = [
  {
    id: 'information-we-collect',
    title: 'Information We Collect',
    body: (
      <>
        <h3>Personal Information</h3>
        <ul>
          <li>Name, email, phone number</li>
          <li>Account credentials</li>
          <li>Profile information</li>
        </ul>
        <h3>Usage Data</h3>
        <ul>
          <li>IP address</li>
          <li>Device and browser information</li>
          <li>Interaction data (pages visited, features used)</li>
        </ul>
        <h3>Sensitive Data (if applicable)</h3>
        <p>If our platform handles care-related information:</p>
        <ul>
          <li>Health or support-related inputs</li>
          <li>Communication records</li>
        </ul>
      </>
    ),
  },
  {
    id: 'how-we-use',
    title: 'How We Use Information',
    body: (
      <>
        <p>We use your data to:</p>
        <ul>
          <li>Provide and operate the Platform</li>
          <li>Improve user experience</li>
          <li>Communicate updates and support</li>
          <li>Ensure security and prevent fraud</li>
        </ul>
      </>
    ),
  },
  {
    id: 'legal-bases',
    title: 'Legal Bases (GDPR, if applicable)',
    body: (
      <>
        <p>We process data based on:</p>
        <ul>
          <li>Consent</li>
          <li>Contractual necessity</li>
          <li>Legitimate interest</li>
          <li>Legal obligations</li>
        </ul>
      </>
    ),
  },
  {
    id: 'sharing',
    title: 'Sharing of Information',
    body: (
      <>
        <p>We may share data with:</p>
        <ul>
          <li>Service providers (hosting, analytics, etc.)</li>
          <li>Legal authorities when required</li>
          <li>Business partners (only as needed to deliver services)</li>
        </ul>
        <div className="callout"><strong>We do not sell personal data.</strong></div>
      </>
    ),
  },
  {
    id: 'retention',
    title: 'Data Retention',
    body: (
      <>
        <p>We retain data only as long as necessary to:</p>
        <ul>
          <li>Provide services</li>
          <li>Meet legal obligations</li>
          <li>Resolve disputes</li>
        </ul>
      </>
    ),
  },
  {
    id: 'security',
    title: 'Data Security',
    body: (
      <p>
        We implement industry-standard safeguards to protect your data, though no system is completely
        secure. Healthcare platforms typically emphasize strong confidentiality practices.
      </p>
    ),
  },
  {
    id: 'your-rights',
    title: 'Your Rights',
    body: (
      <>
        <p>Depending on your location, you may have rights to:</p>
        <ul>
          <li>Access your data</li>
          <li>Correct inaccuracies</li>
          <li>Delete your data</li>
          <li>Restrict or object to processing</li>
          <li>Data portability</li>
        </ul>
        <h3>For California residents (CCPA/CPRA)</h3>
        <ul>
          <li>Right to know what data is collected</li>
          <li>Right to delete</li>
          <li>Right to opt out of data sharing</li>
        </ul>
      </>
    ),
  },
  {
    id: 'cookies',
    title: 'Cookies and Tracking',
    body: (
      <>
        <p>We use cookies and similar technologies to:</p>
        <ul>
          <li>Improve performance</li>
          <li>Analyze usage</li>
          <li>Personalize experience</li>
        </ul>
      </>
    ),
  },
  {
    id: 'third-party',
    title: 'Third-Party Links',
    body: <p>We are not responsible for the privacy practices of external websites.</p>,
  },
  {
    id: 'children',
    title: "Children's Privacy",
    body: (
      <p>
        The Platform is not intended for users under 13. We do not knowingly collect data
        from children.
      </p>
    ),
  },
  {
    id: 'international-transfers',
    title: 'International Data Transfers',
    body: (
      <p>
        If applicable, your data may be processed outside your country with appropriate safeguards.
      </p>
    ),
  },
  {
    id: 'changes',
    title: 'Changes to Privacy Policy',
    body: (
      <p>
        We may update this policy periodically. Continued use means you accept the changes.
      </p>
    ),
  },
  {
    id: 'contact',
    title: 'Contact',
    body: (
      <p>
        Email: <a href="mailto:help@supportcare.io">help@supportcare.io</a>
      </p>
    ),
  },
];

function PrivacyPolicy() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <LegalLayout
      eyebrowIcon="shield"
      eyebrowText="Legal"
      title="Privacy"
      titleEm="Policy"
      lede="How we collect, use, and protect your information when you use SupportCare. We're committed to clear, plain-language privacy practices."
      lastUpdated="May 5, 2026"
      intro={(
        <>
          <strong>The short version:</strong> we collect only what we need to help you check
          eligibility and get paid, we never sell your data, and we use industry-standard
          security to keep it safe. Read on for the full detail.
        </>
      )}
      sections={SECTIONS}
    />
  );
}

export default PrivacyPolicy;
